import React, {useState} from 'react'
import moreOptionIcon from '../../../../../_metronic/assets/images/svg/data-room/more-options-icon.svg'
import redirectLinkIcon from '../../../../../_metronic/assets/images/svg/data-room/redirect-link-icon.svg'
import {useIntl} from 'react-intl'
import {DateTime} from 'luxon'
import {ShareLinkDropdown} from './ShareLinkDropDown'
import {toast} from 'react-toastify'
import {updateShareLinkStatus} from '../../core/_requests'
import OutsideClickHandler from 'react-outside-click-handler'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../../hooks/gtmHelpers'
import {useAuth} from '../../../auth'

function ShareDrawerTableRow({data, getAllShareLinksByDataRoomId, contentId, dataRoomId}: any) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [showShareLinkModal, setShareLinkShowModal] = useState(false)
  const [, setShareLinkUpdateApiLoading] = useState(false)
  const [isLinkChecked, setIsLinkChecked] = useState(data.enabled)
  const {currentUser, companyId} = useAuth()

  const handleCopyToClipboard = (link: string) => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success(formatMessage({id: 'Link copied to clipboard'}))
      })
      .catch(() => {
        toast.error(formatMessage({id: 'Failed to copy link to clipboard'}))
      })
  }

  function getTimeDifference(date: any) {
    const minutes = Math.abs(
      Math.floor(
        date &&
          DateTime.utc().diff(DateTime.fromISO(date), ['days', 'hours', 'minutes']).toObject()
            .minutes
      )
    )

    const days = Math.abs(
      Math.floor(
        date &&
          DateTime.utc().diff(DateTime.fromISO(date), ['days', 'hours', 'minutes']).toObject().days
      )
    )

    const hours = Math.abs(
      Math.floor(
        date &&
          DateTime.utc().diff(DateTime.fromISO(date), ['days', 'hours', 'minutes']).toObject().hours
      )
    )
    if (days === 0 && hours === 0) {
      return `${minutes} ${formatMessage({id: 'min ago'})}`
    }
    if (days === 0) {
      return `${hours} ${formatMessage({id: 'hrs ago'})}`
    } else if (days === 7) {
      return `${formatMessage({id: '1 week'})}`
    } else if (days < 7) {
      return `${days} ${formatMessage({id: 'days ago'})}`
    } else {
      return DateTime.fromISO(date).toFormat('MMM dd')
    }
  }

  const handleToggleShareLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCheckedValue = event.target.checked
    setIsLinkChecked(newCheckedValue)
    editShareLinkValue(newCheckedValue)
  }

  const editShareLinkValue = async (newCheckedValue: boolean) => {
    const updatedShareLinkValues = {
      allowDownload: data.allowDownload,
      requireEmail: data.requireEmail,
      account: data.account,
      emailAuthentication: data.emailAuthentication,
      aclEnabled: data.aclEnabled,
      enabled: newCheckedValue,
      ...(data.dataRoomId && {dataRoomId: data.dataRoomId}),
      ...(data.contentId && {contentId: data.contentId}),
    }

    try {
      setShareLinkUpdateApiLoading(true)
      if (dataRoomId) {
        const {
          data: {success, errors},
        } = await updateShareLinkStatus(data.id, updatedShareLinkValues)
        if (success) {
          toast.success(formatMessage({id: 'Share Link Status Updated Successfully'}))
          getAllShareLinksByDataRoomId()
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateShareLinkStatusSuccessful,
              action: 'normal',
              metadata: {
                dataroom_id: dataRoomId,
                allow_download: data?.allowDownload,
                require_email: data?.requireEmail,
                account: data?.account,
                email_authentication: data?.emailAuthentication,
                acl_enabled: data?.aclEnabled,
                enabled: newCheckedValue,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
              },
            },
          })
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateShareLinkStatusFailed,
              action: 'normal',
              metadata: {
                dataroom_id: dataRoomId,
                allow_download: data?.allowDownload,
                require_email: data?.requireEmail,
                account: data?.account,
                email_authentication: data?.emailAuthentication,
                acl_enabled: data?.aclEnabled,
                enabled: newCheckedValue,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                error: errors[0],
              },
            },
          })
        }
      } else {
        if (contentId) {
          const {
            data: {success, errors},
          } = await updateShareLinkStatus(data.id, updatedShareLinkValues)
          if (success) {
            toast.success(formatMessage({id: 'Share Link Status Updated Successfully'}))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.UpdateShareLinkStatusSuccessful,
                action: 'normal',
                metadata: {
                  content_id: contentId,
                  allow_download: data?.allowDownload,
                  require_email: data?.requireEmail,
                  account: data?.account,
                  email_authentication: data?.emailAuthentication,
                  acl_enabled: data?.aclEnabled,
                  enabled: newCheckedValue,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                },
              },
            })
          } else {
            errors.forEach((error: string) => {
              toast.error(formatMessage({id: error}))
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.UpdateShareLinkStatusFailed,
                action: 'normal',
                metadata: {
                  content_id: contentId,
                  allow_download: data?.allowDownload,
                  require_email: data?.requireEmail,
                  account: data?.account,
                  email_authentication: data?.emailAuthentication,
                  acl_enabled: data?.aclEnabled,
                  enabled: newCheckedValue,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  error: errors[0],
                },
              },
            })
          }
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setShareLinkUpdateApiLoading(false)
    }
  }

  const handleRedirectLinkIconClick = () => {
    const linkUrl = `${window.location.origin}/data-rooms/public/${data.uuid}`
    window.open(linkUrl, '_blank')
  }

  const handleRedirectLinkIconClickFile = () => {
    const linkUrl = `${window.location.origin}/contents/public/${data.uuid}`
    window.open(linkUrl, '_blank')
  }

  const {formatMessage} = useIntl()
  return (
    <tr>
      <td className='share-flex-wrapper account flex-grow-1 flex-md-grow-0 w-200px'>
        <div className='account-box d-flex flex-row flex-md-column align-item-center align-items-md-start'>
          <p className='text-dark m-0 font-size-13'> {data.account}</p>
          <span className='text-dark font-size-10'>
            {`${formatMessage({id: 'updated '})} ${getTimeDifference(data.updatedAt)}`}
          </span>
        </div>
      </td>

      <td className='share-flex-wrapper link flex-grow-1 '>
        <div className='link-box mb-2'>
          <button className='btn btn-light d-flex flex-row align-items-center button_eclipse h-40px'>
            <span className='span_eclipse '>
              {dataRoomId ? (
                <span className='text-dark'>{`${window.location.origin}/data-rooms/public/${data.uuid}`}</span>
              ) : (
                <span className='text-dark'>{`${window.location.origin}/contents/public/${data.uuid}`}</span>
              )}
            </span>
          </button>
          <div
            className='copy-to-clipboard-wrapper bg-white w-100 h-40px'
            onClick={() =>
              handleCopyToClipboard(
                dataRoomId
                  ? `${window.location.origin}/data-rooms/public/${data.uuid}`
                  : `${window.location.origin}/contents/public/${data.uuid}`
              )
            }
          >
            {formatMessage({id: 'Copy to Clipboard'})}
          </div>
        </div>
      </td>

      <td className='share-flex-wrapper action flex-grow-1 flex-md-grow-0 '>
        <div className='action-box d-flex flex-row align-items-center justify-content-between justify-content-md-evenly'>
          <div className='card-option-wrapper'>
            <button
              type='button'
              className='btn btn-sm card-option btn-icon btn-color-primary btn-active-light-primary'
              onClick={dataRoomId ? handleRedirectLinkIconClick : handleRedirectLinkIconClickFile}
            >
              <img src={redirectLinkIcon} alt=''></img>
            </button>
          </div>

          <label className='toggle' htmlFor={`${data.uuid}`}>
            <input
              className='toggle__input'
              name='toggle-input'
              type='checkbox'
              id={`${data.uuid}`}
              checked={isLinkChecked}
              onChange={handleToggleShareLinkChange}
            />
            <div className='toggle__fill'></div>
          </label>

          <div className='card-option-wrapper'>
            <OutsideClickHandler onOutsideClick={() => setMenuOpen(false)}>
              <button
                type='button'
                id={`${data.id}`}
                className='btn btn-sm card-option btn-icon btn-color-primary btn-active-light-primary'
                onClick={() => {
                  setMenuOpen(!menuOpen)
                }}
              >
                <img src={moreOptionIcon} alt=''></img>
              </button>
              <ShareLinkDropdown
                isOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                shareLinkId={data.id}
                setShareLinkShowModal={setShareLinkShowModal}
                showShareLinkModal={showShareLinkModal}
                getAllShareLinksByDataRoomId={getAllShareLinksByDataRoomId}
              />
            </OutsideClickHandler>
          </div>
        </div>
      </td>
    </tr>
  )
}

export default ShareDrawerTableRow
